import { IcoClose24 } from '@onefootprint/icons';
import * as RadixDialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { cx } from 'class-variance-authority';
import type React from 'react';
import { type JSX, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import IconButton from '../../../../icon-button';
import Overlay from '../../../../overlay';
import SearchInput from '../../../../search-input';
import Text from '../../../../text';
import type { BaseSelectOption } from '../../base-select.types';
import type { ItemProps } from './components/item';
import Item from './components/item';

type PickerProps = {
  open: boolean;
  onClose: () => void;
  height: number;
  id?: string;
  placeholder?: string;
  options?: BaseSelectOption[];
  value?: BaseSelectOption;
  renderEmptyState: () => JSX.Element;
  onChange: (newValue: BaseSelectOption) => void;
  OptionComponent?: React.ComponentType<ItemProps>;
};

const Picker = ({
  open,
  onClose,
  height,
  id,
  placeholder,
  value,
  options,
  renderEmptyState,
  onChange,
  OptionComponent = Item,
}: PickerProps) => {
  const { t } = useTranslation('ui', { keyPrefix: 'components.picker' });
  const [search, setSearch] = useState('');
  const [hasScroll, setHasScroll] = useState(false);

  const handleScroll = (e: React.UIEvent<HTMLElement>) => {
    const target = e.target as HTMLElement;
    setHasScroll(target.scrollTop > 0);
  };

  const filteredOptions = useMemo(() => {
    if (!search) {
      return options;
    }
    return options?.filter(option => option?.label.toLowerCase().includes(search.toLowerCase()));
  }, [search, options]);

  useEffect(() => {
    if (!open) {
      setSearch('');
    }
  }, [open]);

  const handleClose = () => {
    onClose();
  };

  return (
    <RadixDialog.Root open={open} onOpenChange={handleClose}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay asChild>
          <Overlay isVisible={open} />
        </RadixDialog.Overlay>
        <RadixDialog.Content aria-describedby={undefined} asChild>
          <div
            className={cx(
              'fixed left-0 bottom-0 w-full bg-primary rounded-t-2xl z-[50]',
              'animate-[bottomSheetIn_150ms_cubic-bezier(0.16,1,0.3,1)]',
              'data-[state=closed]:animate-[bottomSheetOut_150ms_cubic-bezier(0.16,1,0.3,1)]',
            )}
            style={{ height: `${height}px` }}
          >
            <div className="h-10 pt-4 flex justify-center items-center relative">
              <RadixDialog.Close asChild>
                <div className="flex items-center justify-center absolute top-2 right-2">
                  <IconButton aria-label="close" variant="ghost" onClick={handleClose} icon={IcoClose24} />
                </div>
              </RadixDialog.Close>
              {/* @ts-ignore: fails for dashboard project due to i18next selector */}
              <Text variant="label-2">{t('placeholder-default')}</Text>
              <RadixDialog.Title asChild>
                {/* @ts-ignore: fails for dashboard project due to i18next selector */}
                <VisuallyHidden>{t('aria-label-default')}</VisuallyHidden>
              </RadixDialog.Title>
            </div>
            <div className="p-4">
              <div className={cx('pb-4', { 'border-b border-tertiary': hasScroll })}>
                <SearchInput
                  placeholder={placeholder}
                  id={id}
                  onChangeText={setSearch}
                  onReset={() => setSearch('')}
                  tabIndex={0}
                  value={search}
                  data-dd-privacy="mask"
                  className="min-h-10"
                />
              </div>
              <div className="overflow-y-auto" style={{ maxHeight: `${height - 100}px` }} onScroll={handleScroll}>
                {filteredOptions?.length
                  ? filteredOptions.map(option => (
                      <OptionComponent
                        key={option.value}
                        option={option}
                        value={value}
                        onSelect={() => onChange(option)}
                      />
                    ))
                  : renderEmptyState()}
              </div>
            </div>
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
};

export default Picker;
